import api from 'api';
import {
  EResponseTypes,
  ERequestTypes,
  EUser,
  ESnapshotExists
} from 'lib/types';
import { PaymentMethods } from 'lib/types/invoices';
import { OrderModel } from 'lib/model/objects/orderModel';
import { safeAsync } from 'lib/safeWrappers';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { logInfo } from 'utils/logger';

export const requestNewOrderInvoice = async (
  orderId: string,
  version: number,
  force: boolean
): Promise<ResponseOrError<string>> => {
  logInfo('Requesting new order invoice', { orderId, version });
  const {
    response,
    error: createError
  }: EResponseTypes['orders/create-invoice'] = await api.safePost(
    'orders/create-invoice',
    {
      orderId,
      version,
      force
    }
  );

  if (createError !== null) {
    return wrapError(new Error(createError));
  }

  const { eventId } = response!;

  return wrapSuccess(eventId);
};

export const finalizeOrderInvoice = async (
  invoiceId: string
): Promise<ResponseOrError<void>> => {
  const { error: finalizeError } = await api.safePost(
    'orders/finalize-invoice',
    {
      invoiceId
    }
  );

  if (finalizeError !== null) {
    return wrapError(new Error(finalizeError));
  }

  return wrapSuccess(undefined);
};

/**
 * The publisher has taken payment from the customer in person by cash or check
 */
export const markOrderInvoiceAsPaidDirectToPublisher = async ({
  orderModel,
  invoiceId,
  isIOC,
  paymentNotes,
  user
}: {
  orderModel: OrderModel;
  invoiceId: string;
  isIOC: boolean;
  paymentNotes: string;
  user: ESnapshotExists<EUser> | null | undefined;
}): Promise<ResponseOrError<void>> => {
  const logData = { orderId: orderModel.id, invoiceId };
  const [emailError, customerEmail] = await safeAsync(() =>
    orderModel.getBillingEmail()
  )();
  if (emailError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      emailError,
      'Error fetching customer email for order invoice mark paid',
      logData
    );
    return wrapError(emailError);
  }
  const reqData: ERequestTypes['payments/mark-invoice-paid'] = {
    invoiceId,
    customer_email: customerEmail,
    ...(isIOC
      ? { paymentMethod: 'ioc' as PaymentMethods }
      : {
          paymentMethod: 'paid-to-publisher' as PaymentMethods,
          manualPaymentDetails: {
            note: paymentNotes,
            userId: user?.id || ''
          }
        })
  };
  logInfo('Marking order invoice as paid direct to publisher', {
    invoiceId: reqData.invoiceId,
    isIOC
  });
  const { error: failedToMarkAsPaid } =
    await api.safePost<'payments/mark-invoice-paid'>(
      'payments/mark-invoice-paid',
      reqData
    );
  if (failedToMarkAsPaid) {
    return wrapError(new Error(failedToMarkAsPaid));
  }
  return wrapSuccess(undefined);
};
